import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Tag as TagIcon } from "lucide-react";
import { listTopics, setEmailLabel } from "@/lib/emailApi";
import type { EmailDoc } from "@/lib/types";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Label, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";

const ATTENDEE = "Attendee email";

/** The badge of an attendee email: a transactional email (class logistics) sent to a list as a broadcast. */
export function AttendeeBadge({ className }: { className?: string }) {
  return (
    <Badge variant="warning" className={className} title="A transactional email for people who need it (class logistics): it goes even to people who unsubscribed from marketing">
      {ATTENDEE}
    </Badge>
  );
}

/** An email's label (Brandon, 2026-10-30): Transactional, its marketing topic, "No topic yet" or Test. */
export function LabelChip({ label, className }: { label?: string | null; className?: string }) {
  if (!label) return null;
  const variant = label === "Transactional" || label === ATTENDEE ? "warning" : label === "No topic yet" ? "muted" : label === "Test" ? "outline" : "default";
  return (
    <Badge variant={variant} className={className} title="Email label: Transactional, its marketing topic, or No topic yet">
      {label}
    </Badge>
  );
}

const TRANSACTIONAL_WARNING =
  "Transactional email goes even to people who unsubscribed from this brand's email, and topic choices no longer apply. Bounced, complained and blocked addresses still never get it.";

/** The label with a "Change" button: kind (automation and transactional emails only) and topic, with a required
 *  reason; each change is kept in the email's history. */
export function EmailLabelControl({ email }: { email: EmailDoc }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="inline-flex items-center gap-1.5">
      <LabelChip label={email.label} />
      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setOpen(true)} title="Change whether this email is transactional or marketing, and its topic">
        <TagIcon className="size-3.5" /> Change label
      </Button>
      {open && <LabelDialog email={email} onClose={() => setOpen(false)} />}
    </span>
  );
}

function LabelDialog({ email, onClose }: { email: EmailDoc; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const kindChangeable = email.kind === "automation" || email.kind === "transactional";
  const [kind, setKind] = useState<"marketing" | "transactional">(email.kind === "transactional" ? "transactional" : "marketing");
  const [topicId, setTopicId] = useState<number | "">(email.topic_id ?? "");
  const [reason, setReason] = useState("");
  const topics = useQuery({ queryKey: ["email-topics", email.brand_id], queryFn: () => listTopics(email.brand_id), retry: false });
  const toTransactional = kind === "transactional" && email.kind !== "transactional";
  const kindChanged = kindChangeable && (kind === "transactional") !== (email.kind === "transactional");
  const topicChanged = kind === "marketing" && (topicId || null) !== (email.topic_id ?? null);
  const save = useMutation({
    mutationFn: () =>
      setEmailLabel(email.id, {
        kind: kindChanged ? kind : undefined,
        topic_id: kind === "marketing" && topicChanged ? topicId || 0 : undefined,
        reason: reason.trim(),
        confirm: toTransactional,
      }),
    onSuccess: (e) => {
      void qc.invalidateQueries({ queryKey: ["email-doc", email.id] });
      void qc.invalidateQueries({ queryKey: ["broadcasts"] });
      toast(`Label is now ${e.label}`);
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  const history = email.label_history ?? [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(560px,calc(100vw-2rem))] space-y-3">
        <DialogTitle>Change this email's label</DialogTitle>
        <DialogDescription>
          Now: <LabelChip label={email.label} />. Each change is kept with who, when and why.
        </DialogDescription>
        {kindChangeable ? (
          <div className="space-y-1">
            <Label htmlFor="lbl-kind">Kind</Label>
            <Select id="lbl-kind" value={kind} onChange={(e) => setKind(e.target.value as "marketing" | "transactional")}>
              <option value="marketing">Marketing (opt-outs and topic choices apply)</option>
              <option value="transactional">Transactional (receipts, sign-in links, purchase thank-yous)</option>
            </Select>
          </div>
        ) : (
          <p className="text-xs text-[var(--color-muted-foreground)]">A broadcast is always marketing; only its topic can change.</p>
        )}
        {kind === "marketing" && (
          <div className="space-y-1">
            <Label htmlFor="lbl-topic">Topic</Label>
            <Select id="lbl-topic" value={topicId} onChange={(e) => setTopicId(e.target.value ? Number(e.target.value) : "")}>
              <option value="">No topic yet</option>
              {(topics.data ?? [])
                .filter((t) => t.active || t.id === topicId)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </Select>
            <p className="text-xs text-[var(--color-muted-foreground)]">People who turned this topic off in their email preferences won't get this email.</p>
          </div>
        )}
        {toTransactional && (
          <p role="alert" className="rounded-md bg-[var(--color-warning-bg)] p-2 text-sm text-[var(--color-warning-fg)]">
            {TRANSACTIONAL_WARNING}
          </p>
        )}
        <div className="space-y-1">
          <Label htmlFor="lbl-reason">Reason (required)</Label>
          <Textarea id="lbl-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Purchase thank-you: Brandon 2026-10-30" />
        </div>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant={toTransactional ? "destructive" : "default"}
            disabled={save.isPending || !reason.trim() || (!kindChanged && !topicChanged)}
            onClick={() => save.mutate()}
          >
            {toTransactional ? "Make it transactional" : "Save label"}
          </Button>
        </div>
        {history.length > 0 && (
          <div className="space-y-1 border-t border-[var(--color-border)] pt-2">
            <div className="text-xs font-medium text-[var(--color-muted-foreground)]">History</div>
            <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
              {history.map((h, i) => (
                <li key={i}>
                  <span className="text-[var(--color-muted-foreground)]">{h.changed_at_mountain}, {h.changed_by}:</span> {h.from} to {h.to}. {h.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
